import { create } from 'zustand'
import type { TerminalEventWithSeq } from '../../../../shared/types/terminal-event.js'
import { dbgEvent } from '../lib/debug'

interface TerminalSessionState {
  liveEvents: TerminalEventWithSeq[]
  historicalEvents: TerminalEventWithSeq[]
  /**
   * Erreurs client-only (TERM), journal-minidev-tout-afficher-terminal-pulsar-2026-08-11 :
   * texte d'erreur déjà reçu sur le fil (message:error ou status:changed avec champ error)
   * mais jusqu'ici jeté avant tout rendu (cf. messages.handler.ts:onError et
   * subscriptions.ts:routeStatus). Volontairement SÉPARÉ de tout cache lu par DISC
   * (trpc.messages.list) — ce champ n'existe que pour TERM, jamais persisté en BDD, jamais
   * lu par MessageList.tsx. atMs sert à l'intercaler par timestamp dans TerminalView, même
   * principe que createdAtMs pour les messages user (BUG1).
   */
  clientErrors: Array<{ message: string; atMs: number }>
}

interface TerminalStoreState {
  sessions: Record<string, TerminalSessionState>
  appendLiveEvent: (sessionId: string, event: TerminalEventWithSeq) => void
  pruneConfirmedLiveEvents: (sessionId: string, confirmedSeqs: ReadonlySet<number>) => void
  loadHistoricalEvents: (sessionId: string, events: TerminalEventWithSeq[]) => void
  /** Ajout pur, ne touche à aucun autre champ de la session (journal-minidev-tout-afficher-terminal-pulsar-2026-08-11). */
  appendClientError: (sessionId: string, message: string) => void
}

const EMPTY_SESSION: TerminalSessionState = { liveEvents: [], historicalEvents: [], clientErrors: [] }

export const useTerminalStore = create<TerminalStoreState>((set) => ({
  sessions: {},

  appendLiveEvent: (sessionId, event) => {
    dbgEvent('Zustand', 'terminal.appendLiveEvent', { sessionId: sessionId.slice(0, 8), type: event.type })
    set(state => {
      const session = state.sessions[sessionId] ?? EMPTY_SESSION
      return {
        sessions: {
          ...state.sessions,
          [sessionId]: {
            ...session,
            liveEvents: [...session.liveEvents, event],
          },
        },
      }
    })
  },

  appendClientError: (sessionId, message) => {
    dbgEvent('Zustand', 'terminal.appendClientError', { sessionId: sessionId.slice(0, 8), message })
    set(state => {
      const session = state.sessions[sessionId] ?? EMPTY_SESSION
      return {
        sessions: {
          ...state.sessions,
          [sessionId]: {
            ...session,
            clientErrors: [...session.clientErrors, { message, atMs: Date.now() }],
          },
        },
      }
    })
  },

  /**
   * Purge SEULEMENT les liveEvents confirmes persistes (seq present dans confirmedSeqs,
   * calcule par terminal.handler.ts a partir de l'historique BDD frais). Ne fait JAMAIS
   * un wipe total : un event dont le seq n'est pas (encore) confirme reste dans liveEvents,
   * y compris ceux d'un tour N+1 demarre pendant qu'on attendait la confirmation du tour N.
   * Fix race reconciliation TERM, cf. debug-vue-terminale-reconstruction-pulsar-2026-08-10.
   * confirmedSeqs vide (refetch en echec ou sans nouvelle donnee) -> no-op, rien n'est perdu.
   */
  pruneConfirmedLiveEvents: (sessionId, confirmedSeqs) => {
    set(state => {
      const session = state.sessions[sessionId]
      if (!session || session.liveEvents.length === 0) return state
      const pruned = session.liveEvents.filter(
        e => e.seq === null || e.seq === undefined || !confirmedSeqs.has(e.seq),
      )
      if (pruned.length === session.liveEvents.length) return state
      return {
        sessions: {
          ...state.sessions,
          [sessionId]: { ...session, liveEvents: pruned },
        },
      }
    })
  },

  loadHistoricalEvents: (sessionId, events) => {
    set(state => {
      const session = state.sessions[sessionId] ?? EMPTY_SESSION
      return {
        sessions: {
          ...state.sessions,
          [sessionId]: { ...session, historicalEvents: events },
        },
      }
    })
  },
}))
